/**
 * DropDown
 * @class
 * @constructor
 * @public
 * @version 1.1.0
 */
class DropDown extends HTMLElement {
	constructor() {
		super();
	
		this.toggle = this.toggle.bind(this);
		this.open = this.open.bind(this);
		this.close = this.close.bind(this);
		this.onKeydown = this.onKeydown.bind(this);
		this.onButtonKeydown = this.onButtonKeydown.bind(this); // Add this line
		this.onClick = this.onClick.bind(this);
		this.onFocusout = this.onFocusout.bind(this);
	
		this._isOpen = this.getAttribute('open') === 'true' || false;
	}

	/**
	 * Getters
	 */

	/**
	 * @type HTMLButtonElement | null
	 */
	get button() {
		return this.querySelector('button');
	}

	/**
	 * @type HTMLElement | null
	 */
	get dropdownElement() {
		return document.getElementById(this.dropdownElementId);
	}

	/**
	 * @type string
	 */
	get dropdownElementId() {
		return (
			this.button?.getAttribute('aria-controls') ||
			this.button?.getAttribute('popovertarget') ||
			''
		);
	}

	/**
	 * @returns boolean
	 */
	get isOpen() {
		return this._isOpen;
	}

	/**
	 * set isOpen
	 * @param {boolean} open - Should it be open or not
	 */
	set isOpen(open) {
		this._isOpen = open;
		this.updateUi();
	}

	/**
	 * @type boolean
	 */
	get shouldCloseOnBlur() {
		return this.getAttribute('data-close-on-blur') !== 'false';
	}

	/**
	 * @type boolean
	 */
	get shouldFocusOnOpen() {
		return this.getAttribute('data-focus-on-open') === 'true';
	}

	/**
	* connectedCallback
	* Render html/css & attach event listeners
	*/
	connectedCallback() {
		if (!this.button?.getAttribute('type')) {
			this.button?.setAttribute('type', 'button');
		}
		this.button?.setAttribute('aria-controls', this.dropdownElementId);
		this.button?.addEventListener('mousedown', this.toggle);
		this.button?.addEventListener('keydown', this.onButtonKeydown); // Add this line
		document.addEventListener('keydown', this.onKeydown);
		document.addEventListener('click', this.onClick);
		this.addEventListener('drop-down:open', this.open);
		this.addEventListener('drop-down:close', this.close);
		this.addEventListener('focusout', this.onFocusout);
	
		this.removePopoverAttributes();
		this.updateUi();
	}
 
	/**
	 * disconnectedCallback
	 * Remove listeners.
	 */
	disconnectedCallback() {
		this.button?.removeEventListener('mousedown', this.toggle);
		this.button?.removeEventListener('keydown', this.onButtonKeydown); // Add this line
		document.removeEventListener('keydown', this.onKeydown);
		document.removeEventListener('click', this.onClick);
		this.removeEventListener('drop-down:open', this.open);
		this.removeEventListener('drop-down:close', this.close);
		this.removeEventListener('focusout', this.onFocusout);
	
		this.restorePopoverAttributes();
	}

	/**
	* Handle keyboard interactions on the button
	* @param {KeyboardEvent} event
	*/
	onButtonKeydown(event) {
		const { key } = event;
		if (key === 'Enter' || key === ' ') {
			event.preventDefault(); // Prevent default space/enter behavior
			this.toggle();
		}
	}

	/**
	 * Open the dropdown
	 */
	open() {
		this.isOpen = true;
	}

	/**
	 * Close the dropdown
	 */
	close() {
		this.isOpen = false;
	}

	/**
	 * Toggles the dropdown.
	 */
	toggle() {
		this.isOpen = !this.isOpen;
	}

	/**
	 * Close on esc press
	 * @param {KeyboardEvent} event
	 */
	onKeydown(event) {
		const { key } = event;
		if (key === 'Escape') {
			this.close();
		}
	}

	/**
	 * Close on click outside
	 * @param {MouseEvent} event
	 */
	onClick(event) {
		if (!this.shouldCloseOnBlur || !this.isOpen) {
			return;
		}
		const target = /** @type {HTMLElement} */ (event.target);
		if (!this.contains(target) || target.classList.contains('layover-inner')) {
			this.close();
		}
	}

	/**
	 * Close on focus outside
	 * @param {FocusEvent} event
	 */
	onFocusout(event) {
		if (!this.shouldCloseOnBlur) {
			return;
		}
		const relatedTarget = /** @type {HTMLElement} */ (event.relatedTarget);
		if (this.isOpen && relatedTarget && !this.contains(relatedTarget)) {
			this.close();
		}
	}

	/**
	 * updateUi
	 * Updates the UI based on the dropdown state and handles body scroll.
	 */
	updateUi() {
		this.button?.setAttribute('aria-expanded', this.isOpen.toString());
		const baseLabel = this.button?.getAttribute('data-label-base') || '';
		if (this.isOpen) {
			this.dropdownElement?.removeAttribute('hidden');
			if (this.shouldFocusOnOpen) {
				setTimeout(() => {
					this.querySelector('input')?.focus();
				}, 200);
			}
			this.button?.setAttribute('aria-label', `Stäng ${baseLabel}`);
		} else {
			this.dropdownElement?.setAttribute('hidden', 'true');
			this.button?.setAttribute('aria-label', `Öppna ${baseLabel}`);
		}
	}

	/**
	 * Removes popover attributes to progressively enhance
	 * the dialog with JavaScript.
	 */
	removePopoverAttributes() {
		if (this.button) {
			this.button.dataset.popovertarget =
				this.button.getAttribute('popovertarget') || undefined;
			this.button.removeAttribute('popovertarget');
		}
		this.dropdownElement?.removeAttribute('popover');
	}

	/**
	 * Restores the popover attributes
	 */
	restorePopoverAttributes() {
		if (this.button) {
			this.button.setAttribute(
				'popovertarget',
				this.button.dataset.popovertarget || ''
			);
			delete this.button.dataset.popovertarget;
		}
		this.dropdownElement?.setAttribute('popover', '');
	}
}

if (customElements.get('drop-down') === undefined) {
	window.customElements.define('drop-down', DropDown);
}
